import { KakaoBrandMessageButton, StoreDoc, UserDoc } from '@gooduncles/gu-app-schema';
import { Button, Checkbox, Input, notification, Select } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { FC, useMemo, useState } from 'react';
import { storeStateList } from 'src/constants';
import { isProduction } from 'src/constants/firebase.constants';
import useSWRUsers from 'src/hooks/swr/useSWRUsers';
import useStoresForSearch from 'src/hooks/swr/useStoresForSearch';
import { callKakaoBrandMessage } from 'src/utils/firebase/firebase-callable';

import classes from './BrandMessage.module.scss';

const { TextArea } = Input;

const MAX_CONTENT_LENGTH = 1300;
const MAX_BUTTON_NAME_LENGTH = 28;

type UserWithStore = UserDoc & {
  store?: StoreDoc;
};

const getUsersWithStore = (users: UserDoc[] | undefined, stores: StoreDoc[] | undefined): UserWithStore[] => {
  if (!users) return [];
  if (!stores) return users;

  return users.map((user) => {
    const store = stores.find((store) => store._id === user.storeId);
    return { ...user, store };
  });
};

const BrandMessage: FC = () => {
  const { users, usersLoading } = useSWRUsers([['kakaoNotification', '==', true]]);
  const { stores } = useStoresForSearch();

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [content, setContent] = useState('');
  const [hasButton, setHasButton] = useState(false);
  const [buttonName, setButtonName] = useState('');
  const [buttonUrl, setButtonUrl] = useState('');
  const [sending, setSending] = useState(false);

  const usersWithStore = useMemo(() => getUsersWithStore(users, stores), [users, stores]);

  const userOptions = useMemo(() => {
    return usersWithStore
      .filter((user) => user.userTel)
      .map((user) => {
        const storeState = user.store?.state ? storeStateList[user.store.state] : '??';
        const storeNickname = user.store?.storeNickname ?? '미등록';
        return {
          label: `[${storeState}] ${storeNickname}_${user.email}`,
          value: user._id,
        };
      });
  }, [usersWithStore]);

  const selectedUsers = useMemo(() => {
    return usersWithStore.filter((user) => selectedUserIds.includes(user._id));
  }, [usersWithStore, selectedUserIds]);

  const isValidForm = useMemo(() => {
    if (selectedUserIds.length === 0) return false;
    if (!content.trim()) return false;
    if (hasButton && (!buttonName.trim() || !buttonUrl.trim())) return false;
    return true;
  }, [selectedUserIds, content, hasButton, buttonName, buttonUrl]);

  const handleUserChange = (value: string[]) => {
    setSelectedUserIds(value);
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
  };

  const handleHasButtonChange = (e: CheckboxChangeEvent) => {
    setHasButton(e.target.checked);
    if (!e.target.checked) {
      setButtonName('');
      setButtonUrl('');
    }
  };

  const handleButtonNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setButtonName(e.target.value);
  };

  const handleButtonUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setButtonUrl(e.target.value);
  };

  const handleSend = async () => {
    if (!isValidForm) return;

    const usersToSend = selectedUsers.filter((user) => user.userTel);
    if (usersToSend.length === 0) {
      notification.warning({
        message: '발송 대상 없음',
        description: '전화번호가 등록된 수신자가 없습니다.',
      });
      return;
    }

    setSending(true);

    const buttons: KakaoBrandMessageButton[] | undefined = hasButton
      ? [
          {
            ordering: 1,
            type: 'WL',
            name: buttonName,
            linkMo: buttonUrl,
            linkPc: buttonUrl,
            target: 'out',
          },
        ]
      : undefined;

    let successCount = 0;
    let failCount = 0;

    for (const user of usersToSend) {
      try {
        if (!isProduction) {
          notification.info({
            message: '[DEV] 브랜드 메시지 발송 스킵',
            description: `수신자: ${user.email} (${user.userTel})\n내용: ${content.trim().slice(0, 50)}${content.trim().length > 50 ? '...' : ''}`,
          });
          successCount++;
          continue;
        }

        const response = await callKakaoBrandMessage({
          recipientNo: user.userTel,
          content: content.trim(),
          chatBubbleType: 'TEXT',
          targeting: 'I',
          senderGroupingKey: 'custom',
          buttons,
        });

        if (response.data.result === 'success') {
          successCount++;
        } else {
          failCount++;
          console.error(`Failed to send to ${user.email}:`, response.data.reason);
        }
      } catch (error) {
        failCount++;
        console.error(`Error sending to ${user.email}:`, error);
      }
    }

    setSending(false);

    if (failCount === 0) {
      notification.success({
        message: '발송 완료',
        description: `${successCount}명에게 메시지를 발송했습니다.`,
      });
      setSelectedUserIds([]);
      setContent('');
      setHasButton(false);
      setButtonName('');
      setButtonUrl('');
    } else {
      notification.warning({
        message: '발송 완료 (일부 실패)',
        description: `성공: ${successCount}건, 실패: ${failCount}건`,
      });
    }
  };

  return (
    <div className={classes.container}>
      <h1>카카오 브랜드 메시지</h1>
      <p>채널 친구에게 브랜드 메시지를 발송합니다.</p>

      <section>
        <h2>수신자 선택</h2>
        <Select
          mode="multiple"
          showSearch
          placeholder="수신자를 선택하세요"
          options={userOptions}
          loading={usersLoading}
          style={{ width: '100%', maxWidth: 600 }}
          value={selectedUserIds}
          onChange={handleUserChange}
          filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
        />
        {selectedUserIds.length > 0 && <p className={classes.recipientCount}>선택된 수신자: {selectedUserIds.length}명</p>}
      </section>

      <div className={classes.divider} />

      <section>
        <h2>메시지 작성</h2>
        <div className={classes.messageContainer}>
          <div className={classes.messageInput}>
            <TextArea
              placeholder="메시지 내용을 입력하세요"
              value={content}
              onChange={handleContentChange}
              maxLength={MAX_CONTENT_LENGTH}
              rows={8}
              showCount
            />
          </div>
          <div className={classes.preview}>
            <div className={classes.previewContent}>{content || '미리보기'}</div>
            {hasButton && buttonName && <div className={classes.previewButton}>{buttonName}</div>}
          </div>
        </div>
      </section>

      <section>
        <h2>버튼 설정</h2>
        <div className={classes.buttonSection}>
          <Checkbox checked={hasButton} onChange={handleHasButtonChange}>
            웹 링크 버튼 추가
          </Checkbox>
          {hasButton && (
            <div className={classes.buttonInputs}>
              <Input
                placeholder="버튼명 (최대 28자)"
                value={buttonName}
                onChange={handleButtonNameChange}
                maxLength={MAX_BUTTON_NAME_LENGTH}
                style={{ width: 200 }}
              />
              <Input
                placeholder="링크 URL"
                value={buttonUrl}
                onChange={handleButtonUrlChange}
                style={{ width: 400 }}
              />
            </div>
          )}
        </div>
      </section>

      <Button
        type="primary"
        size="large"
        className={classes.sendButton}
        onClick={handleSend}
        loading={sending}
        disabled={!isValidForm}
      >
        {sending ? '발송 중...' : `${selectedUserIds.length}명에게 발송하기`}
      </Button>
    </div>
  );
};

export default BrandMessage;
